import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '~/components/ui/Modal';
import { selectionStore } from '~/lib/persistence/selectionStore';
import axios from 'axios';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface GithubAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  githubCreds: {
    GITHUB_CLIENT_ID: string;
    GITHUB_CLIENT_SECRET: string;
  };
  requireAuth?: boolean;
}

interface GitHubUser {
  email: string;
  avatar_url: string;
  name?: string;
}

export default function GithubAuthModal({ isOpen, onClose, githubCreds, requireAuth = false }: GithubAuthModalProps) {
  const { t } = useTranslation();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [user, setUser] = useState<GitHubUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pollInterval = useRef<NodeJS.Timeout>();
  const successTimeout = useRef<NodeJS.Timeout>();
  const MAX_POLL_ATTEMPTS = 15;
  const [isTransitioning, setIsTransitioning] = useState(false);

  const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${
    githubCreds.GITHUB_CLIENT_ID
  }&scope=${encodeURIComponent('repo workflow read:org user user:email')}`;

  const checkGithubToken = () => {
    const pollCount = { current: 0 };

    const checkForToken = async (): Promise<boolean> => {
      const token = selectionStore.get().githubAccessToken;

      if (token) {
        console.log('[checkGithubToken] Found token');

        // Validate token before setting
        const isValid = await fetchUserDetails(token);

        if (!isValid) {
          console.log('[checkGithubToken] Invalid token');
          selectionStore.setKey('githubAccessToken', '');
          setError('GitHub token is invalid or expired. Please reconnect.');
          setAccessToken(null);
          setUser(null);
          setIsTransitioning(false);
          return false;
        }

        setAccessToken(token);

        return true;
      }

      pollCount.current += 1;

      if (pollCount.current >= MAX_POLL_ATTEMPTS) {
        console.log('[checkGithubToken] Max attempts reached');
        setIsTransitioning(false);
        return false;
      }

      return false;
    };

    // Set up polling
    const interval = setInterval(async () => {
      const result = await checkForToken();

      if (result) {
        clearInterval(interval);
      }
    }, 3000);

    return interval;
  };

  const fetchUserDetails = async (token: string) => {
    try {
      const { data } = await axios.get<GitHubUser>('https://api.github.com/user', {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.github+json',
        },
      });

      setUser(data);
      setAccessToken(token);
      return true;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return false;
    }
  };

  const handleGithubLogin = () => {
    setError(''); // Clear any existing errors
    setIsTransitioning(true);
    window.open(githubAuthUrl, '_blank');
    pollInterval.current = checkGithubToken();
  };

  // Add token validation on component mount
  useEffect(() => {
    const validateStoredToken = async () => {
      const token = selectionStore.get().githubAccessToken;

      if (token) {
        const isValid = await fetchUserDetails(token);

        if (!isValid) {
          selectionStore.setKey('githubAccessToken', '');
          setError('GitHub token is invalid or expired. Please reconnect.');
          setAccessToken(null);
          setUser(null);
        }
      }
    };

    validateStoredToken();
  }, []);

  useEffect(() => {
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }

      if (successTimeout.current) {
        clearTimeout(successTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    // If modal is opened for auth requirement, show auth message
    if (requireAuth && isOpen) {
      setAccessToken(null);
      setUser(null);
    }
  }, [requireAuth, isOpen]);

  // When user is present (token valid or newly connected), announce success and show a short transition loader
  useEffect(() => {
    if (requireAuth && user && isOpen) {
      setIsTransitioning(true);
      window.dispatchEvent(new CustomEvent('github:login:success'));
      successTimeout.current = setTimeout(() => {
        setIsTransitioning(false);
      }, 1200);
    }

    return () => {
      if (successTimeout.current) {
        clearTimeout(successTimeout.current);
      }
    };
  }, [requireAuth, user, isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !user) {
      setIsTransitioning(false);
    }
  }, [isOpen, user]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="rounded-lg bg-[#121212] p-6 w-[480px]">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-md font-medium text-white">
            {requireAuth
              ? t('modals.github.authRequired', 'GitHub Authentication Required')
              : t('modals.github.title', 'Import Your Code from GitHub')}
          </h2>
          <button onClick={onClose}>
            <div className="i-ph:x-bold text-2xl bg-white rounded-full p-1" />
          </button>
        </div>
        {requireAuth && (
          <>
            <p className="text-sm text-white/60 mb-4">
              {t('modals.github.connectMessage', 'Please connect with GitHub to access repository features')}
            </p>

            <button
              onClick={handleGithubLogin}
              className={`w-full text-black rounded-lg py-2 ${
                user ? 'bg-green-300' : 'bg-white'
              } hover:brightness-94 transition-colors font-medium flex items-center justify-center gap-2 text-sm`}
              disabled={!!user || isTransitioning}
            >
              {isTransitioning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t('modals.github.finalizing', 'Connecting...')}</span>
                </>
              ) : (
                <>
                  <div className={user ? 'i-ph:check-bold text-2xl' : 'i-ph:github-logo text-2xl'} />
                  {user
                    ? t('modals.github.connectedToGithub', 'Connected to GitHub')
                    : t('modals.github.connectGithub', 'Connect with GitHub')}
                </>
              )}
            </button>
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </>
        )}
      </div>
    </Modal>
  );
}
